import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Row, Col, Typography, DatePicker, Empty } from 'antd';
import { ArrowLeftOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import TableData from '../../components/table';
import { vehicleActions } from '../../actions/vehicleActions';

const { Text } = Typography;

const BatchTripSheets: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const batchId = location.state?.batchId;
  const batchName = location.state?.batchName;

  const tripSheetByBatchState = useSelector((state: any) => state.tripSheetByBatchReducer);

  const [tsDateRange, setTsDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [tsPage, setTsPage] = useState(1);
  const [tsPageSize, setTsPageSize] = useState(5);

  const fetchBatchTripSheets = useCallback(
    (page = 1, size = 5, dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] = [null, null]) => {
      if (!batchId) return;
      const params: any = { pageNumber: page, pageSize: size };
      if (dates[0]) params.startDate = dates[0].format('YYYY-MM-DD');
      if (dates[1]) params.endDate = dates[1].format('YYYY-MM-DD');
      dispatch(vehicleActions.listTripSheetsByBatch({ batchId, params }));
    },
    [dispatch, batchId]
  );

  useEffect(() => {
    if (batchId) fetchBatchTripSheets(1, tsPageSize, [null, null]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const goBack = () => navigate('/batches/view', { state: { batchId } });

  if (!batchId) {
    return (
      <div className="batch-view-page">
        <div className="batch-view-header">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/batches')} className="batch-back-btn">Batches</Button>
        </div>
        <Empty description="No batch context found" style={{ marginTop: 40 }} />
      </div>
    );
  }

  return (
    <div className="batch-view-page">
      <div className="batch-view-header">
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={goBack} className="batch-back-btn">{batchName || 'Batch'}</Button>
      </div>

      <div className="sd-title-row" style={{ marginBottom: 16 }}>
        <h2 className="sd-title"><FileTextOutlined style={{ marginRight: 10 }} />Trip Sheets</h2>
      </div>

      <Card className="entity-details-card" bordered={false}>
        <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <DatePicker.RangePicker
              value={tsDateRange as any}
              onChange={(dates: any) => {
                setTsDateRange(dates || [null, null]);
                setTsPage(1);
                fetchBatchTripSheets(1, tsPageSize, dates || [null, null]);
              }}
              format="DD/MM/YYYY"
              allowClear
            />
          </Col>
        </Row>

        {tripSheetByBatchState?.data?.summary && (
          <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
            <Row gutter={16}>
              <Col span={12}><Text type="secondary">Total Trips:</Text> <Text strong>{tripSheetByBatchState.data.summary.totalTrips}</Text></Col>
              <Col span={12}><Text type="secondary">Total Distance:</Text> <Text strong>{tripSheetByBatchState.data.summary.totalDistance?.toLocaleString()} km</Text></Col>
            </Row>
          </div>
        )}

        <TableData
          className="trip-sheet-table"
          dataSource={(tripSheetByBatchState?.data?.data ?? []).map((item: any) => ({ ...item, key: item._id }))}
          loading={tripSheetByBatchState?.isLoading}
          columns={[
            { title: 'Trip Date', dataIndex: 'tripDate', key: 'tripDate', render: (val: string) => val ? dayjs(val).format('DD/MM/YYYY') : '-' },
            { title: 'Instructor', key: 'instructor', render: (_: any, r: any) => r.instructorFirstName ? `${r.instructorFirstName} ${r.instructorLastName || ''}`.trim() : '-' },
            { title: 'Vehicle', key: 'vehicle', render: (_: any, r: any) => r.vehicleName ? `${r.vehicleName} (${r.vehicleNumberPlate})` : '-' },
            { title: 'Start (km)', dataIndex: 'startOdometer', key: 'startOdometer', render: (val: number) => val?.toLocaleString() ?? '-' },
            { title: 'End (km)', dataIndex: 'endOdometer', key: 'endOdometer', render: (val: number) => val?.toLocaleString() ?? '-' },
            { title: 'Distance (km)', dataIndex: 'totalDistance', key: 'totalDistance', render: (val: number) => val?.toLocaleString() ?? '-' },
            { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', render: (val: string) => val || '-' },
          ]}
          pagination={(tripSheetByBatchState?.data?.totalCount || 0) > 5 ? {
            current: tripSheetByBatchState?.data?.pageNumber || tsPage,
            pageSize: tripSheetByBatchState?.data?.pageSize || tsPageSize,
            total: tripSheetByBatchState?.data?.totalCount || 0,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20'],
            showTotal: (total: number) => `Total ${total} records`,
            onChange: (page: number, size: number) => {
              setTsPage(page);
              setTsPageSize(size);
              fetchBatchTripSheets(page, size, tsDateRange);
            },
          } : false}
        />
      </Card>
    </div>
  );
};

export default BatchTripSheets;
